import { ChangeDetectionStrategy, Component, computed, input, OnInit, signal } from "@angular/core";
import { S25DateModule } from "../../s25-dateformat/s25.date.module";
import { S25Util } from "../../../util/s25-util";
import { UserprefService } from "../../../services/userpref.service";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { S25States } from "../../../services/state/state.map";
import Name = S25States.Name;
import { NgTemplateOutlet } from "@angular/common";
import { S25RouterLinkDirective } from "../../s25-nav-bar/s25.router.link.directive";
import { SeEvent } from "../event/se.event.details.view.component";
import { StudentEngagementService } from "../student.engagement.service";

@TypeManagerDecorator("se-event-tile")
@Component({
    selector: "se-event-tile",
    template: `
        @if (init()) {
            @if (routingStateName()) {
                <a s25-ng-router-link [stateName]="routingStateName()" [stateParams]="stateParam()">
                    <ng-container [ngTemplateOutlet]="eventTile"></ng-container>
                </a>
            } @else {
                <ng-container [ngTemplateOutlet]="eventTile"></ng-container>
            }
        }

        <ng-template #eventTile>
            @if (imageUrl()) {
                <div class="event-image-container">
                    <img [src]="imageUrl()" [alt]="event().itemName + ' event image'" class="event-image" />
                    <div class="event-date-overlay">
                        <span class="date-day">{{ getDay() }}</span>
                        <span class="date-month">{{ getMonth() }}</span>
                    </div>
                </div>
            } @else {
                <div class="event-image-container">
                    <div class="event-image-placeholder">
                        <s25-ng-icon [type]="'calendarDate'" [size]="60"></s25-ng-icon>
                    </div>
                    <div class="event-date-overlay">
                        <span class="date-day">{{ getDay() }}</span>
                        <span class="date-month">{{ getMonth() }}</span>
                    </div>
                </div>
            }

            <div class="event-content">
                <h3 class="event-name">{{ event().itemName }}</h3>

                <div class="event-datetime">
                    <s25-ng-icon [type]="'calendarDate'" [size]="16"></s25-ng-icon>
                    <span class="datetime-text">{{ event().date | datePipe: datePref }}</span>
                </div>

                @if (event().description) {
                    <p class="event-description">{{ event().description }}</p>
                }

                @if (event().location) {
                    <div class="event-location">
                        <s25-ng-icon [type]="'locationMarker'" [size]="16"></s25-ng-icon>
                        <span class="location-text">{{ event().location }}</span>
                    </div>
                }
            </div>
        </ng-template>
    `,
    styles: `
        :host {
            background: white;
            border-radius: 16px;
            overflow: hidden;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
            transition:
                transform 0.3s ease,
                box-shadow 0.3s ease;
            border: 1px solid #f0f0f0;
        }

        :host:hover {
            transform: translateY(-4px);
            box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
        }

        .event-image-container {
            position: relative;
            height: 200px;
            overflow: hidden;
        }

        .event-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }

        .event-image-placeholder {
            width: 100%;
            height: 100%;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .event-date-overlay {
            position: absolute;
            top: 1rem;
            right: 1rem;
            background: rgba(0, 0, 0, 0.8);
            color: white;
            padding: 0.5rem;
            border-radius: 8px;
            text-align: center;
            min-width: 50px;
        }

        .date-day {
            display: block;
            font-size: 1.2rem;
            font-weight: 700;
            line-height: 1;
        }

        .date-month {
            display: block;
            font-size: 0.8rem;
            text-transform: uppercase;
            opacity: 0.9;
            margin-top: 0.25rem;
        }

        .event-content {
            padding: 1.5rem;
        }

        .event-name {
            margin: 0 0 1rem 0;
            font-size: 1.3rem;
            font-weight: 600;
            color: #333;
            line-height: 1.3;
            word-wrap: break-word;
        }

        .event-datetime {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            margin-bottom: 1rem;
            color: #666;
        }

        .datetime-text {
            font-weight: 500;
        }

        .event-description {
            color: #555;
            line-height: 1.5;
            margin: 0 0 1rem 0;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
        }

        .event-location {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            color: #666;
            font-size: 0.9rem;
        }

        .location-text {
            font-weight: 500;
        }

        ::ng-deep .event-image-placeholder svg {
            color: #fff;
            opacity: 0.8;
        }

        ::ng-deep .event-datetime svg,
        ::ng-deep .event-location svg {
            opacity: 0.7;
        }

        @media (max-width: 768px) {
            .event-content {
                padding: 1rem;
            }

            .event-name {
                font-size: 1.2rem;
            }

            .event-image-container {
                height: 160px;
            }
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25DateModule, S25IconComponent, NgTemplateOutlet, S25RouterLinkDirective],
})
export class SeEventTileComponent implements OnInit {
    event = input.required<SeEvent>();
    routingStateName = input<Name>();

    datePref: string = "S25|EEE MMM dd yyyy";
    init = signal(false);
    stateParam = computed(() => ({ itemId: this.event().itemId }));

    imageUrl = computed(() => StudentEngagementService.parseUrl(this.event().imageAttr?.value));

    async ngOnInit() {
        const [datePref, error] = await S25Util.Maybe(UserprefService.getS25Dateformat());
        if (error) return console.error(error);
        this.datePref = datePref;
        this.init.set(true);
    }

    getDay(): string {
        if (!this.event().date) return "";
        try {
            const date = S25Util.date.parse(this.event().date);
            return date.getDate().toString();
        } catch (error) {
            return "";
        }
    }

    getMonth(): string {
        if (!this.event().date) return "";
        try {
            const date = S25Util.date.parse(this.event().date);
            return S25Util.date.getMonthName(date).slice(0, 3);
        } catch (error) {
            return "";
        }
    }
}
